/* ------------------------------------------------------------------
   مجوزها در سمت Frontend — سند مادر بند ۷.۲
   ------------------------------------------------------------------
   ⚠️ هشدار معماری که باید همیشه جلوی چشم باشد:

     «Frontend فقط نمایش را کنترل می‌کند؛
      مجوز نهایی همیشه در Backend Policy اجرا می‌شود.»

   هیچ‌کدام از توابع این فایل کنترل امنیتی نیستند. کاربر می‌تواند
   با تغییر URL یا ابزار توسعه‌دهنده از آنها عبور کند و این پذیرفته
   شده است — چون Backend مستقل بررسی می‌کند (معیار AC-02 و AC-11).
   ------------------------------------------------------------------ */

/** قالب اجباری نام مجوز — بند ۷.۱ : <module>.<resource>.<action> */
export type PermissionKey = string

export type DataScope = 'self' | 'direct_reports' | 'unit' | 'organization'

export interface AuthUser {
  id: string
  uuid: string
  display_name: string
  email: string | null
  mobile: string | null
  avatar_url: string | null
  employment: {
    employee_no: string
    position_title: string | null
    org_unit_name: string | null
    manager_display_name: string | null
  } | null
  roles: Array<{
    key: string
    name: string
    data_scope: DataScope
    /** نقشِ «مدیر سیستم» — فقط برای تصمیم‌های نمایشی، نه استنتاج مجوز */
    is_super_admin?: boolean
  }>
  permissions: PermissionKey[]
  /** بیشترین دامنه داده‌ای کاربر — برای تصمیم‌های نمایشی */
  max_scope: DataScope
  /** آیا اکنون از طرف شخص دیگری اقدام می‌کند (جانشینی) */
  acting_as: { user_id: string; display_name: string } | null
  must_change_password: boolean
  two_factor_enabled: boolean
}

const SCOPE_RANK: Record<DataScope, number> = {
  self: 0,
  direct_reports: 1,
  unit: 2,
  organization: 3,
}

/** آیا کاربر این مجوز را دارد؟ */
export function can(user: AuthUser | null, permission: PermissionKey): boolean {
  if (!user) return false
  return user.permissions.includes(permission)
}

/** آیا کاربر حداقل یکی از این مجوزها را دارد؟ */
export function canAny(user: AuthUser | null, permissions: PermissionKey[]): boolean {
  if (!user || permissions.length === 0) return false
  return permissions.some((permission) => user.permissions.includes(permission))
}

/** آیا کاربر همه این مجوزها را دارد؟ */
export function canAll(user: AuthUser | null, permissions: PermissionKey[]): boolean {
  if (!user || permissions.length === 0) return false
  return permissions.every((permission) => user.permissions.includes(permission))
}

/**
 * آیا کاربر دستِ‌کم یک نقشِ «مدیر سیستم» دارد؟
 *
 * ⚠️ کنترل امنیتی نیست و هیچ مجوزی از آن استنتاج نمی‌شود — فقط برای
 * پنهان کردن ماژول‌های `superAdminOnly` از منو و مسیر (بند ۷.۲).
 *
 * 🔴 عمداً روی پرچمِ خودِ نقش تصمیم می‌گیرد و نه روی رشتهٔ
 * `key === 'system_admin'`: کلید یک نام است و روزی ممکن است نقشِ ادمینِ
 * دومی با نام دیگر ساخته شود، ولی `is_super_admin` همان چیزی است که
 * Backend هم با آن می‌سنجد.
 */
export function isSuperAdmin(user: AuthUser | null): boolean {
  if (!user) return false
  return user.roles.some((role) => role.is_super_admin === true)
}

/**
 * درِ پشتیِ توسعه — آیا قفلِ `superAdminOnly` روی این محیط برداشته شده؟
 *
 * ==================================================================
 * ⚠️ دو شرط، و هر دو لازم‌اند.
 *
 * 🔴 `import.meta.env.DEV` تضمین می‌کند این مسیر در Build محصول اصلاً
 * وجود نداشته باشد: Vite آن را در Build به `false` جایگزین می‌کند و
 * bundler کلِ شرط را حذف می‌کند. پس حتی اگر روزی همین متغیر روی سرور
 * ست شود، هیچ اثری ندارد — چیزی برای روشن‌شدن باقی نمانده است.
 *
 * 🔴 خودِ متغیر هم لازم است تا این باز شدن یک انتخابِ صریح باشد و نه
 * چیزی که هر کسی با `npm run dev` ندانسته بگیردش.
 *
 * ⚠️ و مثل بقیهٔ این فایل، کنترل امنیتی نیست. Backend مستقل بررسی
 * می‌کند، پس اگر کاربرِ لوکال مجوزِ ماژول را نداشته باشد صفحه همچنان
 * ۴۰۳ می‌گیرد؛ این فقط منو و مسیر را باز می‌کند.
 * ==================================================================
 */
function restrictedModulesUnlocked(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_UNLOCK_RESTRICTED_MODULES === '1'
}

/**
 * آیا این ماژولِ `superAdminOnly` برای این کاربر باز است؟
 *
 * ==================================================================
 * 🔴 چرا یک تابعِ مشترک و نه تکرارِ شرط در هر جا.
 *
 * ⚠️ این قاعده در **چهار** جا اعمال می‌شود: سایدبار، کاشیِ داشبورد،
 * جست‌وجوی سریع و گاردِ مسیر. تا امروز هر چهار جا `!superAdminOnly ||
 * isSuperAdmin` را جدا نوشته بودند.
 *
 * 🔴 با افزودنِ استثنای نقش، نوشتنِ دوبارهٔ شرط در چهار جا یعنی دیر یا
 * زود یکی عقب می‌ماند — و نتیجه‌اش بدترین حالتِ ممکن است: منو دیده
 * می‌شود ولی مسیر ۴۰۳ می‌دهد، یا برعکس. همان الگویی که در همین فایل
 * برای `hasModuleAccess` رخ داد و کامنتش هنوز آنجاست.
 * ==================================================================
 */
export function canSeeRestrictedModule(
  user: AuthUser | null,
  module: { superAdminOnly?: boolean; alsoVisibleToRoles?: string[] } | undefined,
): boolean {
  if (!module?.superAdminOnly) return true
  if (restrictedModulesUnlocked()) return true
  if (isSuperAdmin(user)) return true

  const allowed = module.alsoVisibleToRoles ?? []

  if (allowed.length === 0 || !user) return false

  return user.roles.some((role) => allowed.includes(role.key))
}

/** آیا دامنه داده‌ای کاربر حداقل به این سطح می‌رسد؟ */
export function hasScope(user: AuthUser | null, scope: DataScope): boolean {
  if (!user) return false
  return SCOPE_RANK[user.max_scope] >= SCOPE_RANK[scope]
}

/**
 * آیا کاربر هیچ مجوزی در این ماژول دارد؟
 * برای تولید منو استفاده می‌شود (بند ۱۱.۱).
 */
export function hasModuleAccess(
  user: AuthUser | null,
  moduleKey: string,
  /**
   * کلیدهایی که رجیستری برای این ماژول اعلام کرده.
   *
   * ==================================================================
   * 🔴 چرا این پارامتر اضافه شد:
   *
   * پیش‌تر دسترسی فقط از روی **پیشوندِ کلید ماژول** حدس زده می‌شد
   * (`access.` برای ماژول `access`). ولی ماژول «سطح دسترسی» کلیدهایش
   * `platform.role.view` و `platform.permission.view` است — هیچ‌کدام
   * با `access.` شروع نمی‌شوند.
   *
   * ⚠️ نتیجه: کنسول سطح دسترسی برای **همه** بسته بود، حتی برای
   * `system_admin` که هر پنج کلیدش را داشت. منو دیده می‌شد (چون
   * سایدبار از روی مجوزِ زیرمنو تصمیم می‌گیرد) ولی صفحه «دسترسی
   * مجاز نیست» می‌داد — و کسی نمی‌فهمید چرا.
   *
   * 🔴 حدسِ مبتنی بر نام، همان‌جایی می‌شکند که نام‌گذاری استثنا دارد.
   * ==================================================================
   */
  declaredKeys?: string[],
): boolean {
  if (!user) return false

  if (declaredKeys && declaredKeys.length > 0) {
    return declaredKeys.some((key) => user.permissions.includes(key))
  }

  const prefix = `${moduleKey}.`

  return user.permissions.some((permission) => permission.startsWith(prefix))
}

/** استخراج بخش‌های نام مجوز */
export function parsePermission(permission: PermissionKey): {
  module: string
  resource: string
  action: string
} | null {
  const parts = permission.split('.')
  if (parts.length !== 3) return null
  return { module: parts[0], resource: parts[1], action: parts[2] }
}
